import React, { useEffect, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { FaPlus, FaTrash, FaPen, FaLock } from 'react-icons/fa';
import DecimalMonetario from '../decimal';
import { SelectMulti } from '../select-multi';
import { TipoFormulario } from '../../services/constantes';
import { BillingTypeAsaas, BillingTypeOptions } from '../../services/tipos';
import api from '../../services/api';

const MAX_LANCAMENTOS = 5;
const TIPO_ENTRADA = 1;
const TIPO_SAIDA = 2;

export interface LancamentoAvulso {
    id?: string;
    valor: number;
    descricao: string;
    vencimento: string;      // YYYY-MM-DD
    idsCategorias: string[]; // 1 ou 2 categorias
    tipo: number;            // 1 = Entrada, 2 = Saída
    pago: boolean;
    dataPagamento?: string;  // YYYY-MM-DD (quando pago)
    cobrarNoAsaas: boolean;
    /** Forma de pagamento no Asaas. undefined = herda a do aluguel da locação. */
    tipoDePagamento?: BillingTypeAsaas;
    /** Juros ao mês (%) e multa da cobrança no Asaas (multa fixa OU percentual). O Asaas calcula. */
    jurosAoMes?: number;
    multaFixa?: number;
    multaPercentual?: number;
    asaasPaymentId?: string; // vem do backend na edição
    somenteLeitura?: boolean;// vem do backend: já pago ou com cobrança viva no Asaas
}

/**
 * Editor de lançamentos avulsos da locação (grid de Opções Avançadas). Grid-resumo + modal por
 * lançamento. Estado propagado ao pai via ref (mesma razão do ParcelasCaucaoEditor: manter a
 * referência de campo.component estável evita remount/perda de foco).
 *
 * Enquanto o ref não é setado (usuário nunca abriu esta seção), o pai envia lancamentosJson=null e o
 * backend NÃO reconcilia — os avulsos existentes ficam intactos. Ao montar, este componente carrega os
 * avulsos existentes (edição) e chama onChange, sinalizando "seção tocada".
 */
export interface LancamentosLocacaoEditorExtraProps {
    idEmpresa: string;
    idLocacao?: string;
    integradoAsaas: boolean;
    onChange: (lancamentos: LancamentoAvulso[]) => void;
    lancamentosRef?: React.MutableRefObject<LancamentoAvulso[] | null>;
}

interface FieldRendererProps {
    campo: any;
    entidade?: any;
}

interface Categoria { id: string; nome: string; cor?: string; descricao?: string; }

// Data LOCAL, não UTC: toISOString() converte para UTC e, em Brasília (UTC-3), depois das 21h o
// vencimento padrão nascia já como o dia seguinte.
const hoje = () => {
    const d = new Date();
    const mes = `${d.getMonth() + 1}`.padStart(2, '0');
    const dia = `${d.getDate()}`.padStart(2, '0');
    return `${d.getFullYear()}-${mes}-${dia}`;
};

const formatReais = (v: number) =>
    (v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatData = (s: string) => {
    if (!s) return '-';
    const [y, m, d] = s.split('T')[0].split('-');
    return d && m && y ? `${d}/${m}/${y}` : s;
};

const linhaVazia = (): LancamentoAvulso => ({
    valor: 0,
    descricao: '',
    vencimento: hoje(),
    idsCategorias: [],
    tipo: TIPO_ENTRADA,
    pago: false,
    dataPagamento: undefined,
    cobrarNoAsaas: false,
    tipoDePagamento: undefined,
});

// Preto ou branco conforme o contraste com a cor de fundo da categoria.
const corTextoContraste = (hex?: string) => {
    if (!hex) return '#fff';
    const h = hex.replace('#', '');
    if (h.length < 6) return '#fff';
    const r = parseInt(h.substring(0, 2), 16);
    const g = parseInt(h.substring(2, 4), 16);
    const b = parseInt(h.substring(4, 6), 16);
    return (r * 0.299 + g * 0.587 + b * 0.114) > 160 ? '#1f2937' : '#fff';
};

const LancamentosLocacaoEditor: React.FC<FieldRendererProps> = ({ campo }) => {
    const extra = (campo?.extraProps || {}) as LancamentosLocacaoEditorExtraProps;
    const { idEmpresa, idLocacao, integradoAsaas } = extra;
    const onChange = extra.onChange || (() => {});
    const lancamentosRef = extra.lancamentosRef;

    const [lancamentos, setLancamentos] = useState<LancamentoAvulso[]>(() => lancamentosRef?.current ?? []);
    const [categorias, setCategorias] = useState<Categoria[]>([]);
    const [modalAberto, setModalAberto] = useState(false);
    const [edicaoIndex, setEdicaoIndex] = useState<number | null>(null);
    const [rascunho, setRascunho] = useState<LancamentoAvulso>(linhaVazia());
    const [erro, setErro] = useState<string>('');
    const [carregando, setCarregando] = useState(false);
    const [falhaCarga, setFalhaCarga] = useState(false);
    // Guarda QUAL locação já foi hidratada, não apenas "se já hidratou". Um booleano bloqueava a
    // hidratação para sempre: se o componente não desmontar entre uma locação e outra (o modal pode
    // reaproveitar a árvore, e "Opções Avançadas" fica expandido entre aberturas), a grid seguiria
    // exibindo os avulsos da locação ANTERIOR. Qualquer edição a partir dali marcaria a seção como
    // tocada e o save reconciliaria a locação atual contra a lista errada — apagando os avulsos dela
    // e recriando cópias dos da outra.
    const locacaoHidratada = useRef<string | null | undefined>(undefined);

    // Só o usuário "toca" a seção: propagar seta o ref e, a partir daí, o save reconcilia. A
    // hidratação NÃO propaga (usa `carregar`) — se ela falhasse e propagasse a lista vazia, o save
    // seguinte reconciliaria para o conjunto vazio e APAGARIA todos os avulsos editáveis da locação.
    const propagar = (lista: LancamentoAvulso[]) => {
        setLancamentos(lista);
        if (lancamentosRef) lancamentosRef.current = lista;
        onChange(lista);
    };

    // Preenche a grid sem marcar a seção como tocada (ref permanece null → backend não reconcilia).
    const carregar = (lista: LancamentoAvulso[]) => setLancamentos(lista);

    // Carrega categorias da empresa.
    useEffect(() => {
        if (!idEmpresa) return;
        api.get(`/CategoriaPorEmpresa/${idEmpresa}`)
            .then(r => setCategorias(r.data || []))
            .catch(() => setCategorias([]));
    }, [idEmpresa]);

    // Hidrata os avulsos já gravados quando em edição — apenas exibe. Enquanto o usuário não editar
    // nada, o pai envia lancamentosJson=null e o backend deixa os avulsos existentes intactos.
    useEffect(() => {
        if (locacaoHidratada.current === (idLocacao ?? null)) return;
        locacaoHidratada.current = idLocacao ?? null;

        // Criação (ou ref já preenchido por uma abertura anterior desta mesma sessão do modal): nada
        // a hidratar. O ref só será setado quando o usuário adicionar a primeira linha.
        if (!idLocacao) {
            setFalhaCarga(false);
            carregar(lancamentosRef?.current ?? []);
            return;
        }

        // Troca de locação com a grid já preenchida: limpa antes de buscar, para que nenhum frame
        // exiba os lançamentos da locação anterior como se fossem desta.
        carregar([]);
        setFalhaCarga(false);
        setCarregando(true);
        const idDaRequisicao = idLocacao;
        // A resposta só vale se ainda for da locação exibida: numa troca rápida A→B, o GET de A pode
        // chegar depois do de B e preencher a grid de B com os lançamentos de A.
        const respostaObsoleta = () => locacaoHidratada.current !== idDaRequisicao;

        api.get(`/Locacao/LancamentosAvulsos/${idLocacao}`)
            .then(r => {
                if (respostaObsoleta()) return;
                const lista: LancamentoAvulso[] = (r.data || []).map((l: any) => ({
                    id: l.id,
                    valor: l.valor,
                    descricao: l.descricao,
                    vencimento: (l.vencimento || '').split('T')[0],
                    idsCategorias: l.idsCategorias || [],
                    tipo: l.tipo,
                    pago: l.pago,
                    dataPagamento: l.dataPagamento ? l.dataPagamento.split('T')[0] : undefined,
                    cobrarNoAsaas: l.cobrarNoAsaas,
                    jurosAoMes: l.jurosAoMes ?? undefined,
                    multaFixa: l.multaFixa ?? undefined,
                    multaPercentual: l.multaPercentual ?? undefined,
                    asaasPaymentId: l.asaasPaymentId,
                    somenteLeitura: l.somenteLeitura,
                }));
                // Se o usuário já tinha mexido nesta sessão do modal, o que ele digitou vence.
                if (lancamentosRef?.current == null) carregar(lista);
                setFalhaCarga(false);
            })
            .catch(() => {
                if (respostaObsoleta()) return;
                // Não dá para saber quais avulsos existem → a grid fica bloqueada. Editar a partir de
                // uma base desconhecida faria o save reconciliar contra ela e apagar o que existe.
                setFalhaCarga(true);
                carregar([]);
            })
            .finally(() => {
                if (!respostaObsoleta()) setCarregando(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idLocacao]);

    const categoriaPorId = (id: string) => categorias.find(c => c.id === id);

    const abrirNovo = () => {
        setErro('');
        setEdicaoIndex(null);
        setRascunho(linhaVazia());
        setModalAberto(true);
    };

    const abrirEdicao = (idx: number) => {
        setErro('');
        setEdicaoIndex(idx);
        setRascunho({ ...lancamentos[idx] });
        setModalAberto(true);
    };

    const removerLinha = (idx: number) => {
        propagar(lancamentos.filter((_, i) => i !== idx));
    };

    const podeCobrarNoAsaas = integradoAsaas && rascunho.tipo === TIPO_ENTRADA && !rascunho.pago;

    // A multa é fixa OU percentual: o tipo sai de qual dos dois campos está preenchido.
    const tipoMulta: '' | 'percentual' | 'fixa' =
        rascunho.multaPercentual !== undefined ? 'percentual' : rascunho.multaFixa !== undefined ? 'fixa' : '';
    const trocarTipoMulta = (tipo: string) => setRascunho({
        ...rascunho,
        multaPercentual: tipo === 'percentual' ? (rascunho.multaPercentual ?? 0) : undefined,
        multaFixa: tipo === 'fixa' ? (rascunho.multaFixa ?? 0) : undefined,
    });
    const numeroOuVazio = (v: string) => (v === '' ? 0 : Number(v));

    const salvarRascunho = () => {
        if (!rascunho.descricao.trim()) return alertar('Informe a descrição.');
        if (!rascunho.valor || rascunho.valor <= 0) return alertar('Informe um valor maior que zero.');
        if (!rascunho.vencimento) return alertar('Informe o vencimento.');
        if (rascunho.idsCategorias.length === 0) return alertar('Selecione ao menos uma categoria.');
        if (rascunho.pago && !rascunho.dataPagamento) return alertar('Informe a data de pagamento.');

        // Juros/multa só existem na cobrança do Asaas; fora dela não são enviados.
        const vaiCobrar = podeCobrarNoAsaas && rascunho.cobrarNoAsaas;
        const juros = vaiCobrar ? Number(rascunho.jurosAoMes) || 0 : 0;
        const multaPct = vaiCobrar && tipoMulta === 'percentual' ? Number(rascunho.multaPercentual) || 0 : 0;
        const multaFixa = vaiCobrar && tipoMulta === 'fixa' ? Number(rascunho.multaFixa) || 0 : 0;
        if (juros < 0 || multaPct < 0 || multaFixa < 0) return alertar('Juros e multa não podem ser negativos.');
        if (juros > 10) return alertar('Os juros ao mês não podem passar de 10%, que é o máximo aceito pelo Asaas.');
        if (multaPct > 100) return alertar('A multa percentual não pode passar de 100%.');
        if (multaFixa > rascunho.valor) return alertar('A multa fixa não pode ser maior que o valor do lançamento.');

        const normalizado: LancamentoAvulso = {
            ...rascunho,
            descricao: rascunho.descricao.trim(),
            dataPagamento: rascunho.pago ? rascunho.dataPagamento : undefined,
            cobrarNoAsaas: podeCobrarNoAsaas ? rascunho.cobrarNoAsaas : false,
            tipoDePagamento: (podeCobrarNoAsaas && rascunho.cobrarNoAsaas) ? rascunho.tipoDePagamento : undefined,
            jurosAoMes: juros > 0 ? juros : undefined,
            multaPercentual: multaPct > 0 ? multaPct : undefined,
            multaFixa: multaFixa > 0 ? multaFixa : undefined,
        };

        if (edicaoIndex === null) {
            propagar([...lancamentos, normalizado]);
        } else {
            propagar(lancamentos.map((l, i) => (i === edicaoIndex ? normalizado : l)));
        }
        setModalAberto(false);
    };

    const alertar = (msg: string) => { setErro(msg); };

    // O limite é dos lançamentos digitados aqui: os somente leitura (pagos ou com cobrança no Asaas,
    // como os trazidos de uma assinatura do cliente) não ocupam vaga.
    const editaveis = lancamentos.filter((l) => !l.somenteLeitura).length;
    const atingiuLimite = editaveis >= MAX_LANCAMENTOS;
    // Enquanto a lista atual não é conhecida (carregando ou falha no GET), nada pode ser editado:
    // qualquer alteração marcaria a seção como tocada e o save reconciliaria contra uma base errada.
    const bloqueado = carregando || falhaCarga;
    const podeAdicionar = !atingiuLimite && !bloqueado;

    return (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, marginTop: 8, background: '#fafafa' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>Lançamentos da locação</div>
                <button
                    type="button"
                    disabled={!podeAdicionar}
                    onClick={abrirNovo}
                    style={{
                        display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 6,
                        border: 'none', cursor: podeAdicionar ? 'pointer' : 'not-allowed',
                        background: podeAdicionar ? '#1f467e' : '#9ca3af', color: '#fff', fontSize: 13, fontWeight: 600,
                    }}
                >
                    <FaPlus size={12} /> Adicionar lançamento
                </button>
            </div>

            {falhaCarga && (
                <div style={{ marginBottom: 10, padding: '8px 10px', background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 6, fontSize: 12 }}>
                    Não foi possível carregar os lançamentos desta locação. A edição está bloqueada para
                    não apagar lançamentos existentes. Feche e abra a locação novamente para tentar de novo —
                    salvar agora mantém os lançamentos atuais intactos.
                </div>
            )}

            <div style={{ border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#f3f4f6', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>
                    <span style={{ flex: 2, minWidth: 0 }}>Descrição</span>
                    <span style={{ width: 90, flexShrink: 0 }}>Valor</span>
                    <span style={{ width: 90, flexShrink: 0 }}>Vencimento</span>
                    <span style={{ flex: 1, minWidth: 0 }}>Categoria</span>
                    <span style={{ width: 110, flexShrink: 0, textAlign: 'center' }}>Status</span>
                    <span style={{ width: 60, flexShrink: 0 }} />
                </div>

                {lancamentos.length === 0 && (
                    <div style={{ padding: '14px 12px', fontSize: 13, color: '#9ca3af', textAlign: 'center' }}>
                        {carregando ? 'Carregando lançamentos...' : falhaCarga ? 'Lançamentos indisponíveis.' : 'Nenhum lançamento adicionado.'}
                    </div>
                )}

                {lancamentos.map((l, idx) => (
                    <div key={l.id ?? idx} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderTop: '1px solid #f3f4f6', background: l.somenteLeitura ? '#f9fafb' : '#fff' }}>
                        <span style={{ flex: 2, minWidth: 0, fontSize: 13, fontWeight: 600, color: '#1f2937', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {l.tipo === TIPO_SAIDA ? '↓ ' : '↑ '}{l.descricao}
                        </span>
                        <span style={{ width: 90, flexShrink: 0, fontSize: 13, fontWeight: 600, color: l.tipo === TIPO_SAIDA ? '#b91c1c' : '#065f46' }}>{formatReais(l.valor)}</span>
                        <span style={{ width: 90, flexShrink: 0, fontSize: 13, color: '#374151' }}>{formatData(l.vencimento)}</span>
                        <span style={{ flex: 1, minWidth: 0, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            {l.idsCategorias.map(cid => {
                                const c = categoriaPorId(cid);
                                if (!c) return null;
                                return (
                                    <span key={cid} title={c.descricao} style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: c.cor || '#6b7280', color: corTextoContraste(c.cor), whiteSpace: 'nowrap' }}>{c.nome}</span>
                                );
                            })}
                        </span>
                        <span style={{ width: 110, flexShrink: 0, display: 'flex', justifyContent: 'center', gap: 6, alignItems: 'center' }}>
                            <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10, color: l.pago ? '#065f46' : '#92400e', background: l.pago ? '#d1fae5' : '#fef3c7' }}>
                                {l.pago ? 'Pago' : 'Pendente'}
                            </span>
                            {l.cobrarNoAsaas && <span title="Cobrança no Asaas" style={{ fontSize: 10, fontWeight: 700, color: '#0369a1' }}>Asaas</span>}
                            {l.cobrarNoAsaas && ((l.jurosAoMes ?? 0) > 0 || (l.multaPercentual ?? 0) > 0 || (l.multaFixa ?? 0) > 0) && (
                                <span title="Juros/multa cobrados pelo Asaas em caso de atraso" style={{ fontSize: 10, fontWeight: 700, color: '#b45309' }}>juros/multa</span>
                            )}
                        </span>
                        <span style={{ width: 60, flexShrink: 0, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                            {l.somenteLeitura || bloqueado ? (
                                <FaLock size={13} color="#9ca3af" title={l.somenteLeitura ? "Já pago ou com cobrança no Asaas — gerencie na tela de Lançamentos" : "Lista indisponível — edição bloqueada"} />
                            ) : (
                                <>
                                    <FaPen size={13} color="#1f467e" style={{ cursor: 'pointer' }} title="Editar" onClick={() => abrirEdicao(idx)} />
                                    <FaTrash size={13} color="#dc3545" style={{ cursor: 'pointer' }} title="Remover" onClick={() => removerLinha(idx)} />
                                </>
                            )}
                        </span>
                    </div>
                ))}

                <div style={{ padding: '6px 12px', background: '#f0f4ff', borderTop: '2px solid #d6e0f0', fontSize: 12, fontWeight: 600, color: '#1f467e' }}>
                    {editaveis}/{MAX_LANCAMENTOS} lançamentos{lancamentos.length > editaveis ? ` (mais ${lancamentos.length - editaveis} já pago${lancamentos.length - editaveis > 1 ? "s" : ""} ou no Asaas)` : ""}
                </div>
            </div>

            <Modal show={modalAberto} onHide={() => setModalAberto(false)} centered backdrop="static">
                <Modal.Header>
                    <Modal.Title style={{ fontSize: 18 }}>
                        {edicaoIndex === null ? 'Adicionar lançamento' : 'Editar lançamento'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Campo label="Descrição">
                        <input
                            type="text"
                            value={rascunho.descricao}
                            onChange={e => setRascunho({ ...rascunho, descricao: e.target.value })}
                            style={inputStyle}
                        />
                    </Campo>

                    <div style={{ display: 'flex', gap: 12 }}>
                        <Campo label="Valor" style={{ flex: 1 }}>
                            <DecimalMonetario
                                campo={{ nome: 'valorLancamentoAvulso', tipo: 'monetario', required: true, modo: TipoFormulario.tpEdicao }}
                                getFieldValue={() => rascunho.valor}
                                handleInputChange={(_n: string, v: number) => setRascunho({ ...rascunho, valor: v })}
                                largura="100%"
                            />
                        </Campo>
                        <Campo label="Vencimento" style={{ flex: 1 }}>
                            <input type="date" value={rascunho.vencimento} onChange={e => setRascunho({ ...rascunho, vencimento: e.target.value })} style={inputStyle} />
                        </Campo>
                    </div>

                    <Campo label="Tipo">
                        <select value={rascunho.tipo} onChange={e => setRascunho({ ...rascunho, tipo: Number(e.target.value), cobrarNoAsaas: Number(e.target.value) === TIPO_ENTRADA ? rascunho.cobrarNoAsaas : false })} style={inputStyle}>
                            <option value={TIPO_ENTRADA}>Entrada (receita)</option>
                            <option value={TIPO_SAIDA}>Saída (despesa)</option>
                        </select>
                    </Campo>

                    <Campo label="Categoria(s)">
                        <SelectMulti
                            dataSource={categorias}
                            initialSelected={categorias.filter(c => rascunho.idsCategorias.includes(c.id))}
                            onSelectionChange={(sel: any[]) => setRascunho(prev => ({ ...prev, idsCategorias: sel.map(c => c.id) }))}
                            optionLabelField="nome"
                            modo={TipoFormulario.tpEdicao}
                            maxRecords={2}
                            optionBackgroundField="cor"
                        />
                    </Campo>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14 }}>
                        <input id="lanc-pago" type="checkbox" checked={rascunho.pago}
                            onChange={e => setRascunho({ ...rascunho, pago: e.target.checked, cobrarNoAsaas: e.target.checked ? false : rascunho.cobrarNoAsaas })}
                            style={{ width: 16, height: 16, accentColor: '#1f467e', cursor: 'pointer' }} />
                        <label htmlFor="lanc-pago" style={{ fontSize: 13, color: '#374151', cursor: 'pointer', margin: 0 }}>Já pago?</label>
                        {rascunho.pago && (
                            <>
                                <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginLeft: 8, margin: 0 }}>Data do Pagamento:</label>
                                <input type="date" value={rascunho.dataPagamento ?? ''} onChange={e => setRascunho({ ...rascunho, dataPagamento: e.target.value })} style={{ ...inputStyle, width: 160 }} />
                            </>
                        )}
                    </div>

                    {podeCobrarNoAsaas && (
                        <div style={{ marginTop: 10 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <input id="lanc-asaas" type="checkbox" checked={rascunho.cobrarNoAsaas}
                                    onChange={e => setRascunho({ ...rascunho, cobrarNoAsaas: e.target.checked })}
                                    style={{ width: 16, height: 16, accentColor: '#0369a1', cursor: 'pointer' }} />
                                <label htmlFor="lanc-asaas" style={{ fontSize: 13, color: '#374151', cursor: 'pointer', margin: 0 }}>Cobrar este lançamento no Asaas</label>
                            </div>

                            {/* Sempre visível, e não só depois de marcar a chave: escondido, o operador
                                não descobria que dá para escolher a forma de pagamento do lançamento. */}
                            {(
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, marginLeft: 24, opacity: rascunho.cobrarNoAsaas ? 1 : 0.5 }}>
                                    <label htmlFor="lanc-forma" style={{ fontSize: 13, fontWeight: 600, color: '#374151', margin: 0 }}>Forma de pagamento:</label>
                                    <select
                                        id="lanc-forma"
                                        disabled={!rascunho.cobrarNoAsaas}
                                        value={rascunho.tipoDePagamento ?? ''}
                                        onChange={e => setRascunho({ ...rascunho, tipoDePagamento: e.target.value === '' ? undefined : (Number(e.target.value) as BillingTypeAsaas) })}
                                        style={{ ...inputStyle, width: 200 }}
                                    >
                                        {/* Vazio = herda a do aluguel, que é o caso comum e evita
                                            obrigar uma escolha por lançamento. */}
                                        <option value="">Mesma do aluguel</option>
                                        {BillingTypeOptions.map(o => (
                                            <option key={String(o.valor)} value={String(o.valor)}>{o.label}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {/* Juros e multa vão na cobrança e quem calcula o atraso é o Asaas. */}
                            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginTop: 8, marginLeft: 24, opacity: rascunho.cobrarNoAsaas ? 1 : 0.5 }}>
                                <label htmlFor="lanc-juros" style={{ fontSize: 13, fontWeight: 600, color: '#374151', margin: 0 }}>Juros ao mês (%):</label>
                                <input id="lanc-juros" type="number" min={0} max={10} step={0.01} disabled={!rascunho.cobrarNoAsaas}
                                    value={rascunho.jurosAoMes ?? ''}
                                    onChange={e => setRascunho({ ...rascunho, jurosAoMes: e.target.value === '' ? undefined : Number(e.target.value) })}
                                    style={{ ...inputStyle, width: 90 }} />
                                <label htmlFor="lanc-multa-tipo" style={{ fontSize: 13, fontWeight: 600, color: '#374151', margin: 0, marginLeft: 8 }}>Multa:</label>
                                <select id="lanc-multa-tipo" disabled={!rascunho.cobrarNoAsaas} value={tipoMulta}
                                    onChange={e => trocarTipoMulta(e.target.value)} style={{ ...inputStyle, width: 140 }}>
                                    <option value="">Sem multa</option>
                                    <option value="percentual">Percentual (%)</option>
                                    <option value="fixa">Valor fixo (R$)</option>
                                </select>
                                {tipoMulta !== '' && (
                                    <input type="number" min={0} step={0.01} disabled={!rascunho.cobrarNoAsaas}
                                        aria-label={tipoMulta === 'percentual' ? 'Multa em percentual' : 'Multa em reais'}
                                        value={(tipoMulta === 'percentual' ? rascunho.multaPercentual : rascunho.multaFixa) ?? ''}
                                        onChange={e => setRascunho(tipoMulta === 'percentual'
                                            ? { ...rascunho, multaPercentual: numeroOuVazio(e.target.value) }
                                            : { ...rascunho, multaFixa: numeroOuVazio(e.target.value) })}
                                        style={{ ...inputStyle, width: 100 }} />
                                )}
                            </div>
                        </div>
                    )}

                    {erro && <div style={{ marginTop: 12, padding: '6px 10px', background: '#fef2f2', color: '#b91c1c', borderRadius: 4, fontSize: 12 }}>{erro}</div>}
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="btn btn-secondary" onClick={() => setModalAberto(false)}>Cancelar</button>
                    <button type="button" className="btn btn-primary" style={{ backgroundColor: '#1F467E', borderColor: '#1F467E' }} onClick={salvarRascunho}>Salvar</button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

const inputStyle: React.CSSProperties = { width: '100%', padding: '7px 9px', border: '1px solid #ccc', borderRadius: 5, fontSize: 13, boxSizing: 'border-box' };

const Campo: React.FC<{ label: string; style?: React.CSSProperties; children: React.ReactNode }> = ({ label, style, children }) => (
    <div style={{ marginTop: 10, ...style }}>
        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 4 }}>{label}</label>
        {children}
    </div>
);

export default LancamentosLocacaoEditor;
